import { SortOrderEnum } from '@shared/types';
import { Pagination } from '@ui-kit/miscs';
import { TableColumn } from '@ui-kit/table/_core';
import { orderBy } from 'lodash-es';

function computePagination(currentTake: number, newTake: number, skip: number): Pagination {
    if (newTake !== currentTake) {
        return { skip: 0, take: newTake };
    } else {
        return { skip: skip * currentTake, take: newTake };
    }
}

function resetTableColumns<T>(currentColumns: TableColumn<T>[], defaultColumns: TableColumn<T>[]): TableColumn<T>[] {
    const updatedColumns = currentColumns.map(column => {
        const defaultColumn = defaultColumns.find(def => def.field === column.field);
        if (defaultColumn) {
            return {
                ...column,
                isVisible: defaultColumn.isVisible,
                orderPriority: defaultColumn.orderPriority,
                inSearchResults: defaultColumn.inSearchResults,
            };
        }
        return column;
    });

    return orderBy(updatedColumns, ['orderPriority']) as TableColumn<T>[];
}

function applySorting<T>(
    currentColumns: TableColumn<T>[],
    sortableColumns: TableColumn<T>[],
    defaultSortOrder: SortOrderEnum = SortOrderEnum.ASC
): TableColumn<T>[] {
    return currentColumns.map(column => {
        const index = sortableColumns.findIndex(c => c.field === column.field);
        const sortColumn = sortableColumns[index];

        return {
            ...column,
            isSortingActive: index !== -1,
            sortOrder: sortColumn?.sortOrder || defaultSortOrder,
            sortPriority: index,
        };
    });
}

function getTableColumnsFromView<T>(viewColumns: any[], defaultColumns: TableColumn<T>[]): TableColumn<T>[] {
    const columnsBasedOnView: any[] = (viewColumns || defaultColumns).map(c => {
        return {
            ...(defaultColumns.find(el => el.field === c.field) || c),
            isVisible: viewColumns?.find(el => el.field === c.field)?.isVisible || false,
            isSortingActive: viewColumns?.find(el => el.field === c.field)?.isSortingActive || false,
            sortOrder: viewColumns?.find(el => el.field === c.field)?.sortOrder || SortOrderEnum.ASC,
            sortPriority: viewColumns?.find(el => el.field === c.field)?.sortPriority || null,
        };
    });
    const columnsNotIncludedInView = defaultColumns
        .filter(bItem => !columnsBasedOnView.find(aItem => aItem.field === bItem.field))
        .map(c => {
            return {
                ...c,
                isVisible: false,
            };
        });
    return columnsBasedOnView.concat(columnsNotIncludedInView);
}

function computeDateRangeFromView(input?: (string | Date | null | undefined)[]): Date[] | undefined {
    if (!input || input.length === 0) {
        return undefined;
    }
    if (input.some(d => !d)) {
        return undefined;
    }

    const [first, second] = input as Date[];

    if (input.length === 1 && first) {
        return [first, first];
    }

    return first && second ? [new Date(first), new Date(second)] : undefined;
}

function computeDateRangeForSaveView(input?: Date[]): string[] | undefined {
    if (!input || input.length === 0) {
        return undefined;
    }

    const formatted = input.map(date => {
        if (!date || !(date instanceof Date) || isNaN(date.getTime())) {
            return undefined;
        }

        const month = (date.getMonth() + 1).toString().padStart(2, '0');
        const day = date.getDate().toString().padStart(2, '0');
        const year = date.getFullYear();

        return `${month}/${day}/${year}`;
    });

    if (formatted.some(d => d === undefined)) {
        return undefined;
    }

    return formatted as string[];
}

function mapColumnsForSaveView<T>(columns: TableColumn<T>[]): any[] {
    return columns
        .filter(c => c.isVisible)
        .map(c => {
            return {
                field: c.field,
                isVisible: c.isVisible,
                isSortingActive: c.isSortingActive,
                sortOrder: c.sortOrder,
                sortPriority: c.sortPriority,
            };
        });
}

export const TableOperationsHelper = {
    computePagination,
    resetTableColumns,
    applySorting,
    getTableColumnsFromView,
    computeDateRangeFromView,
    computeDateRangeForSaveView,
    mapColumnsForSaveView,
};
